import { useEffect } from 'react';
import type { Step, StepId } from '../../types';
import { resolveKnownStep } from '../utils/steps';

interface UseWizardRouteSyncParams {
  routeStepId?: StepId;
  activeSteps: Partial<Record<StepId, Step>>;
  startStep: StepId;
  currentStepId: StepId;
  setCurrentStepId: (stepId: StepId) => void;
  history: StepId[];
  pushHistory: (step: StepId) => void;
  resetHistory: () => void;
  navSourceRef: React.MutableRefObject<null | 'programmatic'>;
  lastStepRef: React.MutableRefObject<StepId>;
}

export const useWizardRouteSync = ({
  routeStepId,
  activeSteps,
  startStep,
  currentStepId,
  setCurrentStepId,
  history,
  pushHistory,
  resetHistory,
  navSourceRef,
  lastStepRef
}: UseWizardRouteSyncParams): void => {
  useEffect(() => {
    const stepId = resolveKnownStep(routeStepId, activeSteps, startStep);

    if (navSourceRef.current === 'programmatic') {
      if (currentStepId !== stepId) {
        setCurrentStepId(stepId);
      }
      lastStepRef.current = stepId;
      navSourceRef.current = null;
      return;
    }

    if (lastStepRef.current === stepId) {
      if (currentStepId !== stepId) {
        setCurrentStepId(stepId);
      }
      return;
    }

    const index = history.lastIndexOf(stepId);
    if (index !== -1) {
      resetHistory();
      history.slice(0, index).forEach(previousStep => pushHistory(previousStep));
      lastStepRef.current = stepId;
      if (currentStepId !== stepId) {
        setCurrentStepId(stepId);
      }
      return;
    }

    pushHistory(lastStepRef.current);
    lastStepRef.current = stepId;
    if (currentStepId !== stepId) {
      setCurrentStepId(stepId);
    }
  }, [
    routeStepId,
    activeSteps,
    startStep,
    currentStepId,
    setCurrentStepId,
    history,
    pushHistory,
    resetHistory,
    navSourceRef,
    lastStepRef
  ]);
};
